/**
 * Pending documents on create-provider forms (uploaded after save)
 */
import { useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon, InsertDriveFile as FileIcon } from '@mui/icons-material';
import toast from 'react-hot-toast';
import type { PendingProviderDocument } from '../../types/providerDocument';
import ProviderDocumentDialog from './ProviderDocumentDialog';

interface ProviderDocumentsPendingSectionProps {
  documents: PendingProviderDocument[];
  onChange: (documents: PendingProviderDocument[]) => void;
}

const formatFileSize = (bytes: number) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const ProviderDocumentsPendingSection = ({
  documents,
  onChange,
}: ProviderDocumentsPendingSectionProps) => {
  const [dialogOpen, setDialogOpen] = useState(false);

  const handleAdd = ({ name, file }: { name: string; file: File | null }) => {
    if (!name || !file) {
      toast.error('Document name and file are required');
      return;
    }
    onChange([
      ...documents,
      { tempId: `${Date.now()}-${Math.random()}`, name, file },
    ]);
    setDialogOpen(false);
  };

  const handleRemove = (tempId: string) => {
    onChange(documents.filter((doc) => doc.tempId !== tempId));
  };

  return (
    <Card sx={{ mt: 3 }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Box>
            <Typography variant="h6">Documents</Typography>
            <Typography variant="body2" color="text.secondary">
              Optional — certificates, licenses, or other files. They will be uploaded when you save.
            </Typography>
          </Box>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setDialogOpen(true)}>
            Add document
          </Button>
        </Box>

        {documents.length === 0 ? (
          <Box
            sx={{
              py: 4,
              px: 2,
              textAlign: 'center',
              borderRadius: 2,
              border: '1px dashed',
              borderColor: 'divider',
              bgcolor: 'action.hover',
            }}
          >
            <FileIcon sx={{ fontSize: 36, color: 'text.disabled', mb: 1 }} />
            <Typography color="text.secondary">No documents added yet</Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>File</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {documents.map((doc) => (
                  <TableRow key={doc.tempId}>
                    <TableCell>{doc.name}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        icon={<FileIcon />}
                        label={`${doc.file.name} (${formatFileSize(doc.file.size)})`}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell align="right">
                      <IconButton size="small" color="error" onClick={() => handleRemove(doc.tempId)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </CardContent>

      <ProviderDocumentDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSave={handleAdd}
      />
    </Card>
  );
};

export default ProviderDocumentsPendingSection;
